<template>
  <div class="transfer-stock-detail">
    <div class="d-flex align-items-center">
      <h1 class="h3 d-flex align-items-center">
        <a-button type="link" size="small" class="mr-2" @click="goBackToIndex">
          <a-icon type="left" />
        </a-button>
        {{ $t('transferStock.detailTitle') }}
      </h1>
      <div class="ml-auto d-flex align-items-center">
        <a-tag :color="statusColor(detail.status)">
          {{ normalizeStatus(detail.status) }}
        </a-tag>
      </div>
    </div>

    <a-form-model :model="form">
      <a-card
        :title="$t('transferStock.labels.detailCardTitle')"
        class="w-100 mt-4"
        :bordered="true"
      >
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.numberLabel')">
              <a-input :read-only="true" :value="detail.number" />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.dateLabel')">
              <a-input
                :read-only="true"
                :value="
                  detail.date ? $moment(detail.date).format('DD MMM YYYY') : '-'
                "
              />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-model-item
              :label="$t('transferStock.labels.warehouseFromLabel')"
            >
              <a-input
                :read-only="true"
                :value="getWarehouseName(detail.source_warehouse_id)"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item
              :label="$t('transferStock.labels.warehouseToLabel')"
            >
              <a-input
                :read-only="true"
                :value="getWarehouseName(detail.destination_warehouse_id)"
              />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="24" class="mt-2">
          <a-col :span="24">
            <a-form-model-item :label="$t('transferStock.labels.noteLabel')">
              <a-textarea
                :read-only="true"
                :value="detail.note || ''"
                :auto-size="{ minRows: 3, maxRows: 6 }"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-divider />
        <div class="status-info">
          <template v-if="normalizeStatus(detail.status) === 'CREATED'">
            <div class="info-line">
              {{ $t('transferStock.labels.createdAt') }}:
              {{
                detail.created_at
                  ? $moment(detail.created_at).format('DD MMM YYYY - HH:mm')
                  : '-'
              }}
            </div>
            <div class="info-line">
              {{ $t('transferStock.labels.createdBy') }}:
              {{ detail.created_by?.user_name || '-' }}
            </div>
          </template>
          <template v-else-if="normalizeStatus(detail.status) === 'SHIPPED'">
            <div class="info-line">
              {{ $t('transferStock.labels.sentAt') }}:
              {{
                detail.sent_at
                  ? $moment(detail.sent_at).format('DD MMM YYYY - HH:mm')
                  : '-'
              }}
            </div>
            <div class="info-line">
              {{ $t('transferStock.labels.senderLabel') }}:
              {{ detail.sent_by?.user_name || detail.sender?.user_name || '-' }}
            </div>
          </template>
          <template v-else-if="normalizeStatus(detail.status) === 'RECEIVED'">
            <div class="info-line">
              {{ $t('transferStock.labels.receiptAt') }}:
              {{
                detail.receipt_at
                  ? $moment(detail.receipt_at).format('DD MMM YYYY - HH:mm')
                  : '-'
              }}
            </div>
            <div class="info-line">
              {{ $t('transferStock.labels.receiverLabel') }}:
              {{ detail.receipt_by?.user_name || '-' }}
            </div>
          </template>
        </div>
      </a-card>

      <a-card
        :title="$t('transferStock.labels.itemCardTitle')"
        class="w-100 mt-4"
        :bordered="true"
      >
        <a-table
          style="border: 1px solid #e4e9f0; border-radius: 5px"
          :data-source="items"
          :row-key="
            record =>
              record._row_id ||
              record.id ||
              `${record.catalog_id}.${record.sku}`
          "
          :columns="itemColumns"
          :pagination="false"
          size="middle"
        >
          <template slot="no" slot-scope="text, record, index">
            {{ index + 1 + (params.page - 1) * params.size }}
          </template>
          <template slot="catalog" slot-scope="text, record">
            <div class="catalog-display-line">
              <span class="sku">{{ record.sku?.toUpperCase() || '-' }}</span>
              <span class="title">{{
                record.title || record.catalog_title || '-'
              }}</span>
            </div>
          </template>
          <template slot="quantity" slot-scope="text, record">
            <span
              :style="{
                color:
                  normalizeStatus(detail.status) === 'CREATED' &&
                  Number(record.quantity ?? 0) >
                  Number(record.availability_source ?? 0)
                    ? '#E00000'
                    : '',
              }"
            >
              {{ Number(record.quantity ?? 0) }}
            </span>
          </template>
          <template slot="availability" slot-scope="text, record">
            <span
              :style="{
                color:
                  normalizeStatus(detail.status) === 'CREATED' &&
                  (Number(record.availability_source ?? 0) < 0 ||
                    Number(record.quantity ?? 0) >
                    Number(record.availability_source ?? 0))
                    ? '#E00000'
                    : '',
              }"
            >
              {{ Number(record.availability_source ?? 0) }}
            </span>
          </template>
          <template slot="qty_shipped" slot-scope="text, record">
            <span>
              {{ Number(record.quantity_sent ?? 0) }}
            </span>
          </template>
          <template slot="unit" slot-scope="text, record">
            {{ record.unit || 'PCS' }}
          </template>
          <template slot="qty_received" slot-scope="text, record">
            {{ Number(record.quantity_received ?? 0) }}
          </template>
          <template slot="footer">
            <a-pagination
              v-model="params.page"
              style="display: flex; justify-content: flex-end"
              :page-size="params.size"
              :page-size-options="sizeOptions"
              :total="totalRow"
              :show-total="
                (total, range) =>
                  $t('utils.pagination_show_total', {
                    perpage: `${range[0]} - ${range[1]}`,
                    total,
                  })
              "
              :disabled="isLoading"
              show-size-changer
              :locale="{ items_per_page: $t('utils.items_per_page') }"
              @change="onPageChange"
              @showSizeChange="onShowSizeChange"
            />
          </template>
        </a-table>
      </a-card>
      <div class="mt-3 d-flex align-items-center">
        <div>
          <a-button type="default" @click="goBackToIndex">
            <a-icon type="left" />
          </a-button>
        </div>
        <div class="ml-auto text-right">
          <template v-if="normalizeStatus(detail.status) === 'CREATED'">
            <a-button
              type="primary"
              class="mr-2"
              :loading="isSubmitting"
              :disabled="isSubmitting || hasExceededQty || !isAllowedSource"
              @click="onShipNow"
            >
              {{ $t('transferStock.actions.send') }}
            </a-button>
            <a-button
              type="danger"
              class="mr-2"
              :loading="isSubmitting"
              :disabled="isSubmitting || !isAllowedSource"
              @click="onCancel"
            >
              {{ $t('transferStock.actions.cancel') }}
            </a-button>
            <a-button :disabled="hasExceededQty" @click="onPrintSJ">
              {{ $t('transferStock.actions.printSJ') }}
            </a-button>
          </template>
          <template v-else-if="normalizeStatus(detail.status) === 'SHIPPED'">
            <a-button
              type="primary"
              class="mr-2"
              :loading="isSubmitting"
              :disabled="isSubmitting || !isAllowedDestination"
              @click="onReceive"
            >
              {{ $t('transferStock.actions.receive') }}
            </a-button>
            <a-button
              type="danger"
              :loading="isSubmitting"
              :disabled="isSubmitting || !isAllowedSource"
              @click="onCancel"
            >
              {{ $t('transferStock.actions.cancel') }}
            </a-button>
          </template>
        </div>
      </div>
    </a-form-model>
    <a-modal
      :visible="showPrintModal"
      :title="$t('transferStock.print.title')"
      width="900px"
      :footer="null"
      @cancel="showPrintModal = false"
    >
      <div class="d-flex align-items-center mb-2">
        <div class="mr-2">
          {{ $t('transferStock.print.paperSize') }}
        </div>
        <a-select v-model="paperSize" style="width: 160px">
          <a-select-option value="A4">
            A4
          </a-select-option>
          <a-select-option value="A5">
            A5
          </a-select-option>
          <a-select-option value="Letter">
            Letter
          </a-select-option>
          <a-select-option value="Legal">
            Legal
          </a-select-option>
        </a-select>
        <div class="ml-auto">
          <a-button class="mr-2" @click="showPrintModal = false">
            {{ $t('transferStock.print.close') }}
          </a-button>
          <a-button type="primary" @click="printSJ">
            {{ $t('transferStock.print.print') }}
          </a-button>
        </div>
      </div>
      <div class="sj-preview">
        <div ref="sjPrintPage" class="sj-page" :style="pageStyle">
          <div class="sj-header">
            <div class="sj-title">
              {{ $t('transferStock.print.title') }}
            </div>
            <div class="sj-meta">
              <div>
                {{ $t('transferStock.labels.numberLabel') }}:
                <b>{{ detail.number || '-' }}</b>
              </div>
              <div>
                {{ $t('transferStock.labels.dateLabel') }}:
                <b>{{
                  detail.date ? $moment(detail.date).format('DD MMM YYYY') : '-'
                }}</b>
              </div>
            </div>
          </div>
          <div class="sj-section">
            <div class="sj-block">
              <div class="sj-block-title">
                {{ $t('transferStock.print.sender') }}
              </div>
              <div class="sj-block-content">
                <div>{{ getWarehouseName(detail.source_warehouse_id) }}</div>
              </div>
            </div>
            <div class="sj-block">
              <div class="sj-block-title">
                {{ $t('transferStock.print.receiver') }}
              </div>
              <div class="sj-block-content">
                <div>
                  {{ getWarehouseName(detail.destination_warehouse_id) }}
                </div>
              </div>
            </div>
          </div>
          <div class="sj-items">
            <table class="sj-table">
              <thead>
                <tr>
                  <th style="width: 42px">
                    {{ $t('transferStock.itemColumns.no') }}
                  </th>
                  <th>{{ $t('transferStock.itemColumns.catalog') }}</th>
                  <th style="width: 120px">
                    {{ $t('transferStock.itemColumns.quantity') }}
                  </th>
                  <th style="width: 100px">
                    {{ $t('transferStock.itemColumns.unit') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, idx) in items" :key="it.id || idx">
                  <td class="text-center">
                    {{ idx + 1 }}
                  </td>
                  <td>
                    <div class="sj-item-line">
                      <span class="sku">{{
                        (it.sku || '').toUpperCase()
                      }}</span>
                      <span class="title">{{
                        it.title || it.catalog_title || '-'
                      }}</span>
                    </div>
                  </td>
                  <td class="text-right">
                    {{ Number(it.quantity ?? 0) }}
                  </td>
                  <td class="text-center">
                    {{ it.unit || 'PCS' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="sj-footer">
            <div class="sign-col">
              <div class="sign-title">
                {{ $t('transferStock.print.admin') }}
              </div>
              <div class="sign-box" />
              <div class="sign-meta">
                {{ $t('transferStock.print.admin') }}
              </div>
            </div>
            <div class="sign-col">
              <div class="sign-title">
                {{ $t('transferStock.print.driver') }}
              </div>
              <div class="sign-box" />
              <div class="sign-meta">
                {{ $t('transferStock.print.vehicleNumber') }}:
                <b>{{ vehicleNumber }}</b>
              </div>
              <div class="sign-meta">
                {{ $t('transferStock.print.driverName') }}:
                <b>{{ driverName }}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
    <a-modal
      :visible="showErrorModal"
      :title="errorTitle || 'Kesalahan Validasi'"
      width="720px"
      :footer="null"
      @cancel="showErrorModal = false"
    >
      <a-table
        style="border: 1px solid #e4e9f0; border-radius: 5px"
        :columns="errorColumns"
        :data-source="errorItems"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
        size="middle"
        :row-key="(r, i) => i"
      >
        <template slot="item" slot-scope="text">
          <span class="mono-sm" style="white-space: nowrap">
            {{ text }}
          </span>
        </template>
      </a-table>
      <div class="mt-3 text-right">
        <a-button type="primary" @click="showErrorModal = false">
          Tutup
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  getTransferWarehouseDetail,
  cancelTransferNonWms,
  sendTransferNonWms,
  receiveTransferNonWms,
} from '@/api/inventory'
import { getWarehouseInternal } from '@/api/warehouse'

export default {
  name: 'TransferStockDetail',
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      warehouseList: [],
      detail: {
        number: '',
        date: null,
        source_warehouse_id: undefined,
        destination_warehouse_id: undefined,
        note: '',
        status: 'CREATED',
      },
      items: [],
      itemColumns: [],
      totalRow: 0,
      sizeOptions: ['50', '100', '200'],
      params: {
        page: 1,
        size: 50,
      },
      form: {},
      showPrintModal: false,
      paperSize: 'A4',
      driverName: '',
      vehicleNumber: '',
      showErrorModal: false,
      errorTitle: '',
      errorColumns: [
        {
          title: 'Item',
          dataIndex: 'item',
          key: 'item',
          width: 220,
          scopedSlots: { customRender: 'item' },
        },
        { title: 'Alasan', dataIndex: 'reason', key: 'reason' },
      ],
      errorItems: [],
    }
  },
  computed: {
    pageStyle() {
      const map = {
        A4: { w: '210mm', h: '297mm' },
        A5: { w: '148mm', h: '210mm' },
        Letter: { w: '215.9mm', h: '279.4mm' },
        Legal: { w: '215.9mm', h: '355.6mm' },
      }
      const d = map[this.paperSize] || map.A4
      return { width: d.w, minHeight: d.h }
    },
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    validationSummary() {
      if (this.normalizeStatus(this.detail.status) !== 'CREATED') {
        return { negativeAvail: false, negativeQty: false, exceeded: false }
      }
      let negativeAvail = false
      let negativeQty = false
      let exceeded = false
      for (const it of this.items || []) {
        const q = Number(it?.quantity ?? 0)
        const avail = Number(it?.availability_source ?? 0)
        if (avail < 0) negativeAvail = true
        if (q < 0) negativeQty = true
        if (q > avail) exceeded = true
        if (negativeAvail && negativeQty && exceeded) break
      }
      return { negativeAvail, negativeQty, exceeded }
    },
    hasExceededQty() {
      return this.validationSummary.exceeded
    },
    isRestrictionWarehouse() {
      return this.$store.state.user.restriction_base === 'Warehouse'
    },
    permittedWarehouseIds() {
      return (this.$store.state.user.businessList || [])
        .map(b => String(b.warehouse_id || ''))
        .filter(id => id !== '')
    },
    isAllowedSource() {
      if (!this.isRestrictionWarehouse) return true
      const sourceId = String(this.detail.source_warehouse_id || '')
      return this.permittedWarehouseIds.includes(sourceId)
    },
    isAllowedDestination() {
      if (!this.isRestrictionWarehouse) return true
      const destId = String(this.detail.destination_warehouse_id || '')
      return this.permittedWarehouseIds.includes(destId)
    },
  },

  created() {
    this.fetchWarehouses().then(() => this.fetchDetail())
  },
  methods: {
    notifyError(e) {
      const data = e?.response?.data
      const title =
        data?.status ||
        data?.message ||
        e?.response?.status ||
        e?.message ||
        'Error'
      const description =
        data?.error ||
        data?.message ||
        e?.response?.statusText ||
        e?.message ||
        ''
      const errors = data?.data?.errors
      const isValidation =
        data?.message === 'ValidationError' || data?.error === 'ValidationError'
      if (isValidation && errors?.length) {
        this.errorTitle = String(title)
        this.errorItems = errors.map(it => ({
          item: String(it?.item),
          reason: String(it?.reason),
        }))
        this.showErrorModal = true
        return
      }
      this.$notification.error({ message: title, description })
    },
    normalizeStatus(s) {
      const val =
        s == null || s === undefined || s === ''
          ? 'RECEIVED'
          : String(s).toUpperCase()
      return val
    },
    statusColor(s) {
      const v = this.normalizeStatus(s)
      if (v === 'CREATED') return 'geekblue'
      if (v === 'SHIPPED') return 'green'
      if (v === 'RECEIVED') return 'green'
      if (v === 'CANCELED') return 'volcano'
      return 'default'
    },
    async fetchDetail() {
      try {
        this.isLoading = true
        const id = this.$route.params.id
        const { data } = await getTransferWarehouseDetail(id, {
          business_id: this.businessId,
          page: this.params.page,
          size: this.params.size,
        })
        const d = data.data || data || {}
        this.detail = {
          ...this.detail,
          ...d,
        }
        const lines = d.line_items || d.items || []
        this.totalRow =
          data.total_count ??
          data.total_of_elements ??
          (Array.isArray(lines) ? lines.length : 0)
        const isShipped =
          this.normalizeStatus(d.status || this.detail.status) === 'SHIPPED'
        this.items = (lines || []).map(it => ({
          ...it,
          sku: it.sku || it.catalog_sku || it.product_sku,
          title: it.title || it.catalog_title || it.product_title,
          unit: it.unit || 'PCS',
          availability_source: Number(
            it.source_availability ?? it.availability ?? it.onhand_source ?? 0,
          ),
          quantity_sent: Number(it.quantity_sent ?? 0),
          quantity_received: Number(
            isShipped ? it.quantity_sent ?? 0 : it.quantity_received ?? 0,
          ),
        }))
        this.setupItemColumns()
      } catch (e) {
        this.detail = { ...this.detail }
        this.items = []
      } finally {
        this.isLoading = false
      }
    },
    onPageChange(pageNumber) {
      this.params.page = pageNumber
      this.fetchDetail()
    },
    onShowSizeChange(current, pageSize) {
      this.params.page = current
      this.params.size = pageSize
      this.fetchDetail()
    },
    setupItemColumns() {
      const status = this.normalizeStatus(this.detail.status)
      const cols = [
        {
          title: this.$t('transferStock.columns.no'),
          dataIndex: 'no',
          width: 60,
          align: 'center',
          scopedSlots: { customRender: 'no' },
        },
        {
          title: this.$t('transferStock.columns.catalog'),
          dataIndex: 'catalog',
          scopedSlots: { customRender: 'catalog' },
        },
        {
          title: this.$t('transferStock.columns.unit'),
          dataIndex: 'unit',
          width: 100,
          align: 'center',
          scopedSlots: { customRender: 'unit' },
        },
      ]
      if (status === 'CREATED' || status === 'CANCELED') {
        cols.push(
          {
            title: this.$t('transferStock.columns.qtyAvailable'),
            dataIndex: 'availability_source',
            width: 140,
            align: 'right',
            scopedSlots: { customRender: 'availability' },
          },
          {
            title: this.$t('transferStock.columns.qtyTransfer'),
            dataIndex: 'quantity',
            width: 120,
            align: 'right',
            scopedSlots: { customRender: 'quantity' },
          },
        )
      } else if (status === 'SHIPPED' || status === 'RECEIVED') {
        cols.push(
          {
            title: this.$t('transferStock.columns.qtyShipped'),
            dataIndex: 'quantity_sent',
            width: 120,
            align: 'right',
            scopedSlots: { customRender: 'qty_shipped' },
          },
          {
            title: this.$t('transferStock.columns.qtyReceived'),
            dataIndex: 'quantity_received',
            width: 140,
            align: 'right',
            scopedSlots: { customRender: 'qty_received' },
          },
        )
      }
      this.itemColumns = cols
    },
    getWarehouseName(warehouse_id) {
      if (!warehouse_id) return '-'
      const findWarehouse = this.warehouseList.find(
        item => String(item.value) === String(warehouse_id),
      )
      return findWarehouse?.label || '-'
    },
    async fetchWarehouses() {
      try {
        const { data: body } = await getWarehouseInternal({
          business_id: this.businessId,
          limit: 500,
        })
        const raw = body?.data?.data || body?.data || []
        this.warehouseList = raw.map(w => ({
          ...w,
          value: String(w.id || w.warehouse_id),
          label: String(w.name || w.warehouse_name || '').trim(),
        }))
      } catch (e) {
        this.warehouseList = []
      }
    },
    async onShipNow() {
      const number = this.detail?.number
      if (!this.isAllowedSource) {
        this.$message.error(this.$t('transferStock.error.forbiddenSource'))
        return
      }
      if (this.hasExceededQty) {
        this.$message.error(this.$t('transferStock.error.qtyExceededOnSubmit'))
        return
      }
      this.$confirm({
        title: this.$t('transferStock.confirm.sendTitle'),
        content: this.$t('transferStock.confirm.sendContent', {
          value: number || this.$route.params.id,
        }),
        okText: this.$t('transferStock.confirm.okSend'),
        cancelText: this.$t('transferStock.confirm.cancelText'),
        onOk: () => this.doShip(),
      })
    },
    goBackToIndex() {
      this.$router.push({
        name: 'inventory.transfer.stock',
        query: { ...this.$route.query },
      })
    },
    async doShip() {
      this.isSubmitting = true
      try {
        if (this.hasExceededQty) {
          this.$message.error(
            this.$t('transferStock.error.qtyExceededOnSubmit'),
          )
          return
        }
        const items = await this.getAllLineItems()
        const payload = {
          id: String(this.detail.id || this.$route.params.id),
          number: this.detail.number,
          date: this.detail.date
            ? this.$moment(this.detail.date).format('YYYY-MM-DDTHH:mm:ssZ')
            : this.$moment().format('YYYY-MM-DDTHH:mm:ssZ'),
          note: this.detail.note || '',
          type: 'TWH',
          source_warehouse_id: String(this.detail.source_warehouse_id || ''),
          source_business_id: String(this.businessId),
          destination_warehouse_id: String(
            this.detail.destination_warehouse_id || '',
          ),
          destination_business_id: String(this.businessId),
          created_at: this.detail.created_at || null,
          updated_at: this.detail.updated_at || null,
          status: String(this.detail.status || 'CREATED').toUpperCase(),
          sent_by: {
            user_id: this.$store.state.user.id || this.$store.state.user.uuid || '',
            user_name: `${this.$store.state.user.firstName || ''} ${this.$store.state.user.lastName || ''}`.trim() || 'Admin',
          },
          line_items: items,
        }
        await sendTransferNonWms({
          business_id: this.businessId,
          data: payload,
        })
        this.$message.success(this.$t('transferStock.success.send'))
        this.$router.push({
          name: 'inventory.transfer.stock',
          query: { ...this.$route.query },
        })
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    async onReceive() {
      const number = this.detail?.number
      if (!this.isAllowedDestination) {
        this.$message.error(this.$t('transferStock.error.forbiddenDestination'))
        return
      }
      this.$confirm({
        title: this.$t('transferStock.confirm.receiveTitle'),
        content: this.$t('transferStock.confirm.receiveContent', {
          value: number || this.$route.params.id,
        }),
        okText: this.$t('transferStock.confirm.okReceive'),
        cancelText: this.$t('transferStock.confirm.cancelText'),
        onOk: () => this.doReceive(),
      })
    },
    async doReceive() {
      this.isSubmitting = true
      try {
        const items = await this.getAllLineItems()
        const payload = {
          id: String(this.detail.id || this.$route.params.id),
          number: this.detail.number,
          date: this.detail.date
            ? this.$moment(this.detail.date).format('YYYY-MM-DDTHH:mm:ssZ')
            : this.$moment().format('YYYY-MM-DDTHH:mm:ssZ'),
          note: this.detail.note || '',
          type: 'TWH',
          source_warehouse_id: String(this.detail.source_warehouse_id || ''),
          source_business_id: String(this.businessId),
          destination_warehouse_id: String(
            this.detail.destination_warehouse_id || '',
          ),
          destination_business_id: String(this.businessId),
          created_at: this.detail.created_at || null,
          updated_at: this.detail.updated_at || null,
          status: String(this.detail.status || 'SHIPPED').toUpperCase(),
          line_items: items,
        }
        await receiveTransferNonWms({
          business_id: this.businessId,
          data: payload,
        })
        this.$message.success(this.$t('transferStock.success.receive'))
        this.$router.push({
          name: 'inventory.transfer.stock',
          query: { ...this.$route.query },
        })
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    async onCancel() {
      const number = this.detail?.number
      this.$confirm({
        title: this.$t('transferStock.confirm.cancelTitle'),
        content: this.$t('transferStock.confirm.cancelContent', {
          value: number || this.$route.params.id,
        }),
        okText: this.$t('transferStock.confirm.okCancel'),
        cancelText: this.$t('transferStock.confirm.cancelText'),
        onOk: () => this.doCancel(),
      })
    },
    async doCancel() {
      this.isSubmitting = true
      try {
        const items = await this.getAllLineItems()
        const payload = {
          id: String(this.detail.id || this.$route.params.id),
          number: this.detail.number,
          date: this.detail.date
            ? this.$moment(this.detail.date).format('YYYY-MM-DDTHH:mm:ssZ')
            : this.$moment().format('YYYY-MM-DDTHH:mm:ssZ'),
          note: this.detail.note || '',
          type: 'TWH',
          source_warehouse_id: String(this.detail.source_warehouse_id || ''),
          source_business_id: String(this.businessId),
          destination_warehouse_id: String(
            this.detail.destination_warehouse_id || '',
          ),
          destination_business_id: String(this.businessId),
          created_at: this.detail.created_at || null,
          updated_at: this.detail.updated_at || null,
          status: String(this.detail.status || 'CREATED').toUpperCase(),
          line_items: items,
        }
        await cancelTransferNonWms({
          business_id: this.businessId,
          data: payload,
        })
        this.$message.success(this.$t('transferStock.success.cancel'))
        this.$router.push({
          name: 'inventory.transfer.stock',
          query: { ...this.$route.query },
        })
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    async getAllLineItems() {
      try {
        const id = this.$route.params.id
        const size =
          this.totalRow && this.totalRow > 0
            ? this.totalRow
            : this.params.size || 50
        const { data } = await getTransferWarehouseDetail(id, {
          business_id: this.businessId,
          page: 1,
          size,
        })
        const d = data.data || data || {}
        const lines = d.line_items || d.items || []
        return (lines || []).map(it => ({
          id: it.id,
          catalog_id: String(it.catalog_id || ''),
          catalog_title: it.catalog_title || it.title || '',
          quantity: Number(it.quantity ?? 0),
          unit: it.unit || 'PCS',
          sku: it.sku || '',
        }))
      } catch (e) {
        return (this.items || []).map(it => ({
          id: it.id,
          catalog_id: String(it.catalog_id || ''),
          catalog_title: it.catalog_title || it.title || '',
          quantity: Number(it.quantity ?? 0),
          unit: it.unit || 'PCS',
          sku: it.sku || '',
        }))
      }
    },
    onPrintSJ() {
      if (this.hasExceededQty) {
        this.$message.error(this.$t('transferStock.error.qtyExceededOnSubmit'))
        return
      }
      this.showPrintModal = true
    },
    pageDims() {
      const map = {
        A4: { w: '210mm', h: '297mm' },
        A5: { w: '148mm', h: '210mm' },
        Letter: { w: '8.5in', h: '11in' },
        Legal: { w: '8.5in', h: '14in' },
      }
      return map[this.paperSize] || map.A4
    },
    printSJ() {
      try {
        const dims = this.pageDims()
        const html = this.$refs.sjPrintPage?.outerHTML || ''
        const styles = `
          <style>
            @page { size: ${dims.w} ${dims.h}; margin: 12mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .sj-page { width: ${dims.w}; min-height: ${dims.h}; }
            .sj-title { font-size: 20px; font-weight: 700; }
            .sj-meta { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 8px; margin-top: 6px; }
            .sj-section { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 16px; margin: 12px 0; }
            .sj-block-title { font-weight: 600; margin-bottom: 6px; }
            .sj-table { width: 100%; border-collapse: collapse; }
            .sj-table th, .sj-table td { border: 1px solid #ddd; padding: 6px; font-size: 12px; }
            .sj-item-line { display: flex; gap: 8px; }
            .sj-item-line .sku { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; font-weight: 600; }
            .sj-footer { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 16px; margin-top: 24px; }
            .sign-title { font-size: 12px; font-weight: 600; margin-bottom: 4px; }
            .sign-box { height: 80px; border: 1px dashed #999; }
            .sign-meta { font-size: 11px; color: #555; margin-top: 4px; }
          </style>
        `
        const win = window.open('', '_blank')
        win.document.write(
          `<html><head><title>${this.$t(
            'transferStock.print.title',
          )}</title>${styles}</head><body>${html}</body></html>`,
        )
        win.document.close()
        win.focus()
        win.print()
      } catch (e) {
        this.$message.error('Gagal membuka print preview')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.transfer-stock-detail {
  .catalog-display-line {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .catalog-display-line .sku {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      'Liberation Mono', 'Courier New', monospace;
    font-weight: 600;
    margin-right: 8px;
  }
  .catalog-display-line .title {
    color: #333;
  }
  .status-info {
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 6px;
  }
  .info-line {
    font-size: 13px;
    color: #555;
  }
  .catalog-list {
    border: 1px solid #e4e9f0;
    width: 100%;
    border-collapse: collapse;
    tr > th {
      padding: 10px;
      font-weight: 500;
      background-color: #f8f9fa;
      border-bottom: 1px solid #e4e9f0;
      text-align: left;
    }
    tr > td {
      padding: 8px 10px;
      background-color: #fff;
      border-bottom: 1px solid #e4e9f0;
    }
  }
  .mono-sm {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      'Liberation Mono', 'Courier New', monospace;
    font-size: 12px;
  }
}
.sj-preview {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  background: #fafafa;
  padding: 12px;
  border: 1px solid #f0f0f0;
}
.sj-page {
  background: #fff;
  margin: 0 auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 12mm;
}
.sj-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 6px;
  margin-bottom: 6px;
}
.sj-title {
  font-size: 18px;
  font-weight: 700;
}
.sj-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  margin-top: 6px;
}
.sj-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  margin: 12px 0;
}
.sj-block-title {
  font-weight: 600;
  margin-bottom: 6px;
}
.sj-items {
  margin-top: 8px;
}
.sj-table {
  width: 100%;
  border-collapse: collapse;
}
.sj-table th,
.sj-table td {
  border: 1px solid #ddd;
  padding: 6px;
  font-size: 12px;
}
.sj-item-line {
  display: flex;
  gap: 8px;
}
.sj-item-line .sku {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-weight: 600;
}
.sj-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  margin-top: 24px;
}
.sign-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}
.sign-box {
  height: 80px;
  border: 1px dashed #999;
}
</style>
